import React, { useState, useEffect, useContext } from "react";

import {
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Button,
  Skeleton,
} from "antd";

import moment from "moment";
import { useHistory, useParams } from "react-router";
import { ClusterOutlined } from "@ant-design/icons";

import { UserContext } from "../../../../../contexts/UserContext";
import { denuncianteService } from "../../../../../services";
import { openNotification } from "../../../../../components/Notification";
import { SecaoCard } from "../../../../../components/SecaoCard";
import { agravanteService } from "../../../../../services/resources/agravanteService";
import { canalService } from "../../../../../services/resources/canalService";
import { criticidadeService } from "../../../../../services/resources/criticidadeService";

import {
  urls,
  USER_ROLES,
  showPlaceholder as showPlaceholderUTILS,
} from "../../../../../utils";

const DATA_FORMATO = "DD/MM/YYYY";
const ORIGEM_DENUNCIA = "OISOL";

export const Atendimento = ({ handleNext, onView, onEdit, onCreate }) => {
  const history = useHistory();

  const { codigo } = useParams();

  const [atendimento, setAtendimento] = useState(null);

  const [form] = Form.useForm();

  const [loading, setLoading] = useState({
    all: false,
    bairros: false,
    cidades: false,
    submit: false,
  });

  const [protocoloExterno, setProtocoloExterno] = useState(null);
  const [canalSelect, setCanalSelect] = useState(null);

  const [isCanalDefault, setIsCanalDefault] = useState(null);

  const [criticidades, setCriticidades] = useState([]);
  const [canais, setCanais] = useState([]);
  const [agravantes, setAgravantes] = useState([]);
  const { user } = useContext(UserContext);

  const disable =
    onEdit ||
    onView ||
    user?.authorities?.includes(USER_ROLES.INCLUIR_DENUNCIA_INSTITUICAO);

  useEffect(() => {
    getAllEntidades();

    codigo && getAtendimento();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getAtendimento() {
    await denuncianteService
      .getDenunciation(codigo)
      .then(({ data }) => {
        form.setFieldsValue({
          ...data,
          agravantes: data?.agravantes?.map(({ codigo }) => codigo),
          dataOcorrencia:
            data.dataOcorrencia &&
            moment(new Date(data.dataOcorrencia), DATA_FORMATO),
        });

        setAtendimento(form.getFieldsValue());
        setProtocoloExterno(data?.identificadorExterno);
        setCanalSelect(data?.canal?.codigo);
        setIsCanalDefault(data?.canal?.sistema);
      })
      .catch((reason) => {
        openNotification(
          "error",
          <strong>Ocorreu um erro ao buscar os dados do atendimento!</strong>,
          reason?.response?.data?.userMessage
        );
      });
  }

  async function getAllEntidades() {
    try {
      setLoading({ ...loading, all: true });
      const [agravantes, canais, criticidades] = await Promise.all([
        agravanteService.list(),
        canalService.loadCanais(),
        criticidadeService.loadCriticidade(),
      ]);

      setAgravantes(agravantes.data);
      setCanais(canais.data);
      setCriticidades(criticidades.data);

      if (onCreate) {
        const canalSedih = canais?.data?.find(
          (canal) => canal.sigla === "SEDIH"
        );

        if (canalSedih) {
          form.setFieldsValue({
            canal: {
              codigo: canalSedih.codigo,
            },
          });

          setIsCanalDefault(canalSedih.sistema);
          setCanalSelect(canalSedih.codigo);
        }
      }
    } catch (error) {
      openNotification(
        "error",
        <strong>
          Ocorreu um erro ao buscar as entidades relacionadas a denúncia!
        </strong>,
        error.response?.data?.userMessage || ""
      );
    } finally {
      setLoading({ ...loading, all: false });
    }
  }

  async function handleSelectChannel(value) {
    setIsCanalDefault(
      canais?.find((canal) => canal?.codigo === value)?.sistema || false
    );

    setCanalSelect(value);
    checkProtocol(value, protocoloExterno);
  }

  async function checkProtocol(codigoCanal, protocoloExterno) {
    codigoCanal &&
      protocoloExterno &&
      (await denuncianteService
        .getDenunciaByProtocolo(codigoCanal, protocoloExterno)
        .then((response) => {
          if (response?.data?.codigo !== codigo) {
            openNotification(
              "error",
              <strong>
                Já existe uma denúncia com esse protocolo no canal selecionado!
              </strong>,
              <a
                style={{ color: "#00569D" }}
                href={urls.DENUNCIA_DETAILS.replace(
                  ":codigo",
                  response?.data?.codigo
                )}
              >
                <strong>Clique aqui para visualizar a denúncia!</strong>
              </a>,
              10
            );
          }
        })
        .catch(() => {}));
  }

  function transformaDenuncia(denuncia, isCreate = false, isEdit = false) {
    const payload = {
      ...denuncia,
      agravantes: denuncia?.agravantes?.map((codigo) => ({ codigo })),
      descricao: denuncia?.descricao?.trim(),
      criticidade: {
        codigo:
          denuncia.criticidade?.codigo ||
          criticidades.find((x) => x?.descricao === "BAIXA")?.codigo,
      },
      canal: denuncia?.canal?.codigo ? denuncia?.canal : null,
      origemDenuncia: ORIGEM_DENUNCIA,
    };

    if (isCreate || isEdit) {
      delete payload.identificadorExterno;
    }

    return payload;
  }

  async function handleSubmit() {
    setLoading({ ...loading, submit: true });

    const denunciaModificada = transformaDenuncia(
      form.getFieldsValue(),
      onCreate,
      onEdit
    );

    const houveAlteração =
      JSON.stringify(atendimento) !== JSON.stringify(form.getFieldsValue());

    if (onCreate) {
      await denuncianteService
        .createAtendimento(denunciaModificada)
        .then(({ data }) => {
          history.push(
            urls.ATENDIMENTO_EDITAR.replace(":codigo", data?.codigo)
          );
          openNotification(
            "success",
            <strong>Atendimento cadastrado com sucesso!</strong>,
            `A denúncia com protocolo ${
              data?.identificadorExterno
                ? `externo ${data?.identificadorExterno}`
                : `interno ${data?.protocolo}`
            }  foi cadastrado com sucesso e está em análise`
          );
          handleNext("pessoa");
        })
        .catch((reason) =>
          openNotification(
            "error",
            <strong>Ocorreu um erro ao criar o atendimento!</strong>,
            "" + reason?.response?.data?.userMessage
          )
        );
    } else {
      if (onEdit && houveAlteração) {
        await denuncianteService
          .editAtendimento(codigo, denunciaModificada)
          .then(({ data }) => {
            openNotification(
              "success",
              <strong>Atendimento atualizado com sucesso!</strong>,
              `O atendimento com protocolo  ${
                data?.identificadorExterno
                  ? `externo ${data?.identificadorExterno}`
                  : `interno ${data?.protocolo}`
              } foi atualizada`
            );
            handleNext("pessoa");
          })
          .catch((reason) =>
            openNotification(
              "error",
              <strong>Ocorreu um erro ao atualizar o atendimento!</strong>,
              reason?.response?.data?.userMessage
            )
          );
      } else {
        handleNext("pessoa");
      }
    }

    setLoading({ ...loading, submit: false });
  }

  const showPlaceholder = (value) => showPlaceholderUTILS(value, !onView);

  return !loading.all ? (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      name="occurrence"
    >
      <SecaoCard title="Atendimento">
      <Row gutter={32}>
        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            name={["canal", "codigo"]}
            label={<span>Canal:</span>}
            onChange={() => checkProtocol(canalSelect, protocoloExterno)}
            rules={[
              { required: !disable, message: "Este campo é obrigatório!" },
            ]}
          >
            <Select
              placeholder={showPlaceholder("selecionar canal...")}
              autoComplete="none"
              onChange={handleSelectChannel}
              disabled={true}
              allowClear
              showArrow={false}
              showSearch={false}
              filterOption={(input, option) =>
                option?.search?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
              }
              options={canais?.map(({ sigla, codigo, sistema }) => ({
                label: (
                  <>
                    {sistema && <ClusterOutlined />} {sigla}
                  </>
                ),
                value: codigo,
                search: sigla,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            name="identificadorExterno"
            label={<span>Protocolo externo:</span>}
            // rules={[
            //   {
            //     required: !disable && !isCanalDefault,
            //     message: "Este campo é obrigatório!",
            //   },
            // ]}
          >
            <Input
              placeholder={
                isCanalDefault
                  ? showPlaceholder("será gerado automaticamente")
                  : showPlaceholder("Protocolo externo")
              }
              onChange={(e) => setProtocoloExterno(e.target.value)}
              value={protocoloExterno}
              disabled={disable || isCanalDefault || true}
              allowClear
              autoComplete="none"
              autoCorrect="off"
              onBlur={() => checkProtocol(canalSelect, protocoloExterno)}
            />
          </Form.Item>
        </Col>
        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            name="sigilo"
            label={<span>Sigilo:</span>}
            defaultValue={true}
            disabled={true}
          >
            <span>
              <strong>
                Atendimento de cunho sigiloso. Proibido a divulgação de seus
                dados. Este sistema prioriza as diretrizes da Lei Geral de
                Proteção de dados - LGPD
              </strong>
            </span>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span="24">
          <Form.Item
            name="descricao"
            label={<span>Descrição a notificação/ manifestação:</span>}
            rules={[{ required: true, message: "Este campo é obrigatório!" }]}
          >
            <Input.TextArea
              rows={5}
              showCount
              placeholder="O que aconteceu?"
              disabled={onView}
              allowClear
              autoSize={{ minRows: 3 }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={32}>
        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            name="dataOcorrencia"
            label={<span>Data de notificação:</span>}
            // rules={[{ required: true, message: "Este campo é obrigatório!" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format={DATA_FORMATO}
              placeholder="DD/MM/YYYY"
              disabledDate={(d) => !d || d.isAfter(new Date())}
              // disabled={true}
            />
          </Form.Item>
        </Col>

        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item
            name={["criticidade", "codigo"]}
            label={<span>Criticidade:</span>}
          >
            <Select
              placeholder="selecionar..."
              autoComplete="none"
              disabled={onView}
              options={criticidades.map(({ descricao, codigo }) => ({
                label: descricao,
                value: codigo,
              }))}
            />
          </Form.Item>
        </Col>

        <Col xs={{ span: 24 }} lg={{ span: 8 }}>
          <Form.Item name="agravantes" label={<span>Agravante:</span>}>
            <Select
              mode="multiple"
              allowClear
              placeholder="selecionar..."
              disabled={onView}
              options={agravantes.map(({ descricao, codigo }) => ({
                label: descricao,
                value: codigo,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span="24">
          <Form.Item name="observacao" label={<span>Observações:</span>}>
            <Input.TextArea
              rows={5}
              autoSize={{ minRows: 3 }}
              showCount
              placeholder="Algum detalhe que possa facilitar o atendimento da solicitação"
              disabled={onView}
            />
          </Form.Item>
        </Col>
      </Row>
      </SecaoCard>

      <Row justify="end" style={{ marginTop: "1rem" }}>
        <Button htmlType="submit" type="primary" loading={loading.submit}>
          {onCreate ? "cadastrar" : "próximo"}
        </Button>
      </Row>
    </Form>
  ) : (
    <Skeleton active />
  );
};
